import React, { useEffect, Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import CookieConsent, { hasConsent } from './components/CookieConsent';
import AuthGateModal from './components/Auth/AuthGateModal';
import DashboardShell from './components/Layout/DashboardShell';
import { AddressDataProvider } from './contexts/AddressDataContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { GateProvider } from './contexts/GateContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { trackPageView } from './services/analytics';

// Eager load critical pages
import EnhancedLoginForm from './components/Auth/EnhancedLoginForm';
import MobileBottomNav from './components/Navigation/MobileBottomNav';

// Lazy load non-critical pages
const About = lazy(() => import('./pages/About'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const Addresses = lazy(() => import('./pages/Addresses'));
const Alerts = lazy(() => import('./pages/Alerts'));
const GoogleAuthCallback = lazy(() => import('./pages/GoogleAuthCallback'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const SecurityPolicy = lazy(() => import('./pages/SecurityPolicy'));
const Settings = lazy(() => import('./pages/Settings'));
const Docs = lazy(() => import('./pages/Docs'));
const SlackGuide = lazy(() => import('./pages/SlackGuide'));
const Support = lazy(() => import('./pages/Support'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const Blog = lazy(() => import('./pages/Blog'));
const News = lazy(() => import('./pages/News'));
const DashboardOverview = lazy(() => import('./pages/DashboardOverview'));
const Landing = lazy(() => import('./pages/Landing'));
const AgentTools = lazy(() => import('./pages/AgentTools'));
const DashboardNews = lazy(() => import('./pages/dashboard/DashboardNews'));
const DashboardIntegrations = lazy(() => import('./pages/dashboard/DashboardIntegrations'));

// Loading fallback for lazy loaded components
const PageLoader = () => (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
        </div>
    </div>
);

// Google Analytics page view tracker (only tracks if consent given)
const PageViewTracker = () => {
    const location = useLocation();

    useEffect(() => {
        if (hasConsent()) {
            trackPageView(location.pathname + location.search);
        }
    }, [location]);

    return null;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    const location = useLocation();
    const loginRedirect = `/login?next=${encodeURIComponent(location.pathname + location.search)}`;

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return isAuthenticated ? children : <Navigate to={loginRedirect} replace />;
};

/**
 * Legacy-path redirect that preserves the query string, so e.g.
 * `/alerts?slack_connected=true` still lands on `/dashboard/alerts?slack_connected=true`.
 */
const RedirectKeepSearch = ({ to }) => {
    const location = useLocation();
    return <Navigate to={`${to}${location.search}`} replace />;
};

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <AddressDataProvider>
                    {/* Google Analytics page view tracking */}
                    <PageViewTracker />

                    {/* Cookie consent banner (GDPR compliance) */}
                    <CookieConsent />

                    <Suspense fallback={<PageLoader />}>
                        <GateProvider>
                            <AuthGateModal />
                            <Routes>
                                {/* Public */}
                                <Route path="/" element={<Landing />} />
                                <Route path="/news" element={<News />} />
                                <Route path="/products/agent" element={<AgentTools />} />
                                <Route path="/products/x402" element={<Navigate to="/products/agent" replace />} />
                                <Route path="/x402" element={<Navigate to="/products/agent" replace />} />
                                <Route path="/login" element={<EnhancedLoginForm />} />
                                <Route path="/register" element={<RedirectKeepSearch to="/login" />} />
                                <Route path="/verify-email" element={<VerifyEmail />} />
                                <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
                                <Route path="/about" element={<About />} />
                                <Route path="/terms" element={<TermsOfService />} />
                                <Route path="/privacy" element={<PrivacyPolicy />} />
                                <Route path="/security" element={<SecurityPolicy />} />
                                <Route path="/support" element={<Support />} />
                                <Route path="/contact" element={<Support />} />
                                <Route path="/blog" element={<Blog />} />
                                <Route path="/docs" element={<Docs />} />
                                <Route path="/docs/*" element={<Docs />} />
                                <Route path="/help" element={<Docs />} />
                                <Route path="/guide/slack" element={<SlackGuide />} />
                                <Route path="/slack" element={<SlackGuide />} />

                                {/* Legacy redirects (keep ?query so OAuth callbacks survive) */}
                                <Route path="/watchlist" element={<RedirectKeepSearch to="/dashboard/watchlist" />} />
                                <Route path="/addresses" element={<RedirectKeepSearch to="/dashboard/watchlist" />} />
                                <Route path="/alerts" element={<RedirectKeepSearch to="/dashboard/alerts" />} />
                                <Route path="/integrations" element={<RedirectKeepSearch to="/dashboard/integrations" />} />
                                <Route path="/settings" element={<RedirectKeepSearch to="/dashboard/settings" />} />
                                <Route path="/explore" element={<RedirectKeepSearch to="/dashboard" />} />
                                <Route path="/home" element={<RedirectKeepSearch to="/dashboard" />} />
                                <Route path="/dashboard-preview" element={<RedirectKeepSearch to="/dashboard" />} />
                                <Route path="/dashboard-preview/:section" element={<RedirectKeepSearch to="/dashboard" />} />

                                {/* Dashboard: sidebar shell */}
                                <Route element={<DashboardShell />}>
                                    <Route path="/dashboard" element={<DashboardOverview />} />
                                    <Route path="/dashboard/news" element={<DashboardNews />} />
                                    <Route path="/dashboard/watchlist" element={<Addresses />} />
                                    <Route path="/dashboard/alerts" element={<Alerts />} />
                                    <Route path="/dashboard/integrations" element={<ProtectedRoute><DashboardIntegrations /></ProtectedRoute>} />
                                    <Route path="/dashboard/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                                </Route>

                                <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                        </GateProvider>
                    </Suspense>
                    {/* Mobile bottom navigation bar - visible only on small screens */}
                    <MobileBottomNav />
                </AddressDataProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
